/* global process */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { IoMdTime } from "@react-icons/all-files/io/IoMdTime";
import { IoMapOutline } from "@react-icons/all-files/io5/IoMapOutline";
import { FaCreditCard } from "@react-icons/all-files/fa/FaCreditCard";
import { FaCircleInfo } from "@react-icons/all-files/fa6/FaCircleInfo";
import { Button, constants, FormatCurrency } from 'day8-ui';

import { bookingHelpers } from 'day8-react';
import { Svg } from 'day8-react/build/new-components';
import GoogleMapReact from 'google-map-react';
import { getGuestDates } from 'day8-react/build/helpers/week';
import { BOOKING, STANDARD_YACHT } from '../../../../constants';
import { withToolipModal } from '../../../../context/TooltipModalContext';
import Spacer from '../../../../components/Spacer';
import AppItinerary from '../../../../components/booking/AppItinerary';
import { getFeaturedData } from '../../../../helpers/week';

const { TYW_DESTINATION, CUSTOM_BOOKING_DESTINATIONS } = constants;
const FEATURED_ARRIVAL_INFO_ID = 944;

const WP_API_URL = process.env.DAY8_REACT?.WP_API_URL || 'https://wp.theyachtweek.com';

/**
 * WordPress arrival info categories to try for a booking, best first. The category is the week's destination
 * `arrivalInfoSlug` (set per destination in admin); a festival week still inside its parent destination uses its
 * FEATURED_WEEKS slug. Cabin bookings try `<slug>-cabin` first: not every destination has a cabin category.
 *
 * @param {string} subject 'cabin' or 'yacht'
 * @param {object} week the booking's week, with its destination
 * @returns {string[]}
 */
export const getWpSlugs = (subject, week) => {
  const featuredArrivalInfo = getFeaturedData(week)?.arrivalInfo;
  const slug = (featuredArrivalInfo?.length ? `${featuredArrivalInfo}` : null) || week?.destination?.arrivalInfoSlug;
  if (!slug) {
    return [];
  }
  return subject === 'cabin' ? [`${slug}-cabin`, slug] : [slug];
};

/**
 * Id of the first of these WordPress categories that exists, or null.
 *
 * @param {string[]} slugs
 */
const fetchWpCategoryId = async slugs => {
  for (const slug of slugs) {
    const categories = await fetch(`${WP_API_URL}/wp-json/wp/v2/categories?slug=${slug}`).then(response => response.json());
    if (categories && categories[0] && categories[0].id) {
      return categories[0].id;
    }
  }
  return null;
};

class ArrivalInfo extends Component {
  constructor(props) {
    super(props);

    this.state = {
      posts: [],
      cabinPosts: []
    };

    this.fetchPosts = this.fetchPosts.bind(this);
  }

  componentDidMount() {
    this.fetchPosts();
  }

  getWeek() {
    if (this.props.week) {
      return this.props.week;
    }
    return this.props.booking.week;
  }

  sortPosts(posts) {
    return posts.sort((a, b) => b.categories.includes(FEATURED_ARRIVAL_INFO_ID) - a.categories.includes(FEATURED_ARRIVAL_INFO_ID));
  }

  async fetchPosts() {
    try {
      // Do not show arrival info if the booking is not paid
      if (this.props.booking.paymentStatus === "none") {
        return;
      }
      const destinationId = this.getWeek().destination.id;
      if (this.props.booking) {
        const packageData = this.props.booking.package;
        const sessionStorageItem = `arrival-posts-${destinationId}${packageData ? `-${packageData.id}` : ''}-${this.props.booking.subject}`;
        if (sessionStorage.getItem(sessionStorageItem) !== null && this.props.booking !== null) {
          const posts = JSON.parse(sessionStorage.getItem(sessionStorageItem));
          this.setState({ posts });
        } else {
          const categoryId = await fetchWpCategoryId(getWpSlugs(this.props.booking.subject, this.getWeek()));

          if (categoryId) {
            const posts = await fetch(`${WP_API_URL}/wp-json/wp/v2/posts?categories=${categoryId}`).then(response =>
              response.json());

            if (packageData?.id === STANDARD_YACHT.CLASSIC_RATING) {
              const selfCateredPackage = await fetch(`${WP_API_URL}/wp-json/wp/v2/posts?slug=self-catered-package-items`).then(response =>
                response.json());
              if (selfCateredPackage && selfCateredPackage.length) {
                posts.push(selfCateredPackage[0]);
              }
            }
            // sort posts so FEATURED_ARRIVAL_INFO_ID category is first
            this.setState({ posts: this.sortPosts(posts) });
            if (posts.length) {
              sessionStorage.setItem(sessionStorageItem, JSON.stringify(posts));
            }
          }
        }
      } else {
        const cabinCategoryId = await fetchWpCategoryId(getWpSlugs('cabin', this.getWeek()));
        const yachtCategoryId = await fetchWpCategoryId(getWpSlugs('yacht', this.getWeek()));

        if (cabinCategoryId) {
          const cabinPosts = await fetch(`${WP_API_URL}/wp-json/wp/v2/posts?categories=${cabinCategoryId}`).then(response =>
            response.json());

          this.setState({ cabinPosts: this.sortPosts(cabinPosts) });
        }

        if (yachtCategoryId) {
          const yachtPosts = await fetch(`${WP_API_URL}/wp-json/wp/v2/posts?categories=${yachtCategoryId}`).then(response =>
            response.json());

          this.setState({ posts: this.sortPosts(yachtPosts) });
        }
      }
    } catch (err) {
      console.error(err);
    }
  }

  renderEmptyItinerary(weekId, destinationId) {

    let  link = getFeaturedData(weekId)?.itineraryLink || null;
    if (!link && destinationId == TYW_DESTINATION.CROATIA_ORIGINAL) {
      link = "/croatia-original#the-itinerary";
    }
    if (!link && destinationId == TYW_DESTINATION.GREECE) {
      link = "/greece-original#the-itinerary";
    }
    if (!link && destinationId == TYW_DESTINATION.ITALY) {
      link = "/sicily-original#the-itinerary";
    }
    return (
      <div>
        <p>We are adding the final touches to your route - your finalised itinerary will be released a month before you sail.</p>
        {link && (
          <>
            <p>Take a look at our example itinerary here:</p>
            <a href={link} target="_blank" rel="noopener noreferrer"><Button className='dui-mt-4' buttonType='SECONDARY'>Example itinerary</Button></a>
          </>
        )}
      </div>
    );
  }

  render() {
    const { booking, tooltipModalContext } = this.props;
    const { parentBooking } = booking || {};
    const { posts, cabinPosts } = this.state;
    const week = this.getWeek();
    let marina;
    let deposit;

    if (booking && booking.boatWeek) {
      marina = booking.boatWeek.marina;
      deposit = (
        <FormatCurrency
          amount={booking.boatWeek.boat.deposit / 100}
          baseCurrency={booking.boatWeek.boat.currency}
          showCurrency={booking.boatWeek.boat.currency}
        />
      );
    }

    if (booking && parentBooking) {
      marina = parentBooking.boatWeek.marina;
    }

    if (
      booking &&
      booking.bookingType === BOOKING.MYSTERY_TYPE &&
      moment(getGuestDates(booking.week).beginDate)
        .subtract(2, 'weeks')
        .isAfter()
    ) {
      marina = null;
      deposit = null;
    }

    const depositText = 'The security deposit is payable in card or cash to the yachting company during check-in on arrival. It protects against any damages caused to the yacht during the week and is fully refundable, assuming no damage is caused.';

    const Pin = ({ icon }) => <Svg fill="var(--color-error)" className="icon--md  margin-right-md" name={icon} />;
    const company = booking?.week.company.id === 4 ? 'Yachts And Friends' : 'Yacht Week';
    return (
      <div>
        <h2 className='dui-text-2xl'>Arrival Info</h2>
        {booking && (
          <div className='dui-mt-4'>
            <div className='dui-flex dui-items-start dui-gap-2'>
              <IoMapOutline className='dui-shrink-0 dui-mt-1' />
              {marina ? (
                <div className='dui-w-full'>
                Marina: <b>{marina.name}</b>, {marina.city}, {bookingHelpers.getCountryName(marina.country)}
                  {marina.latitude && marina.longitude && (
                    <GoogleMapReact
                      apiKey="AIzaSyDu24LHw_eOpnobo8f35T9COSol1T9TrIY"
                      center={{ lat: marina.latitude, lng: marina.longitude }}
                      zoom={15}
                      style={{
                        height: '300px',
                        width: '100%',
                        position: 'relative',
                        marginTop: 20
                      }}
                    >
                      <Pin
                        lat={marina.latitude}
                        lng={marina.longitude}
                        icon="location-bold"
                      />
                    </GoogleMapReact>
                  )}
                </div>
              ) : (
                CUSTOM_BOOKING_DESTINATIONS.includes(week.destination.id) ? (
                  null
                ) : (
                  <p>
                Your marina will be confirmed and added to this page 2-3 weeks prior to departure.{' '}
                    {booking &&
                  booking.week &&
                  booking.week.destination.id === 1 &&
                  'Split Airport is the best airport to fly in to regardless of marina'}
                  </p>
                ))}
            </div>
            <div className='dui-mt-4 dui-flex dui-items-start dui-gap-2'>
              <IoMdTime className='dui-shrink-0 dui-mt-1' />
              <div>
                <p>
                  <b>Check-in: {week.destination.checkInTime}</b>, {moment.utc(getGuestDates(week).beginDate).format('Do MMMM YYYY')}
                </p>
                {week.destination.checkOutTime && (
                  <p>
                    <b>Check-out: {week.destination.checkOutTime}</b>, {moment.utc(getGuestDates(week).endDate).format('Do MMMM YYYY')}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        {deposit && (
          <div className="dui-mt-4 dui-flex dui-items-start dui-gap-2">
            <FaCreditCard className="dui-shrink-0 dui-mt-1" />
            <div className='dui-flex dui-items-center dui-gap-2'>
              <p>Deposit: </p>
              <Button
                buttonType='LINK_UNDERLINE'
                buttonSize="MEDIUM"
                rightIcon={<FaCircleInfo />}
                onClick={() => tooltipModalContext.openTooltipModal({ title: 'Deposit', tooltipText: depositText })}
              >
                {deposit}
              </Button>
            </div>
          </div>
        )}
        <Spacer />
        <h2 className="dui-text-2xl">
          {(cabinPosts.length > 0 && posts.length > 0) ? `${company} Yacht Information` : `Your Guide To ${company}`}
        </h2>
        {!posts.length > 0 && <p className="dui-mt-4">Guide coming soon</p>}
        {posts.length > 0 && (
          <div className='dui-mt-4 dui-grid dui-grid-cols-1 md:dui-grid-cols-3 dui-gap-8'>
            {posts.map((post, i) => {
              const tywLink = post.featured_image_src;
              return (
                <a key={post.id} href={post.link} className={`${i === 0 ? 'md:dui-col-span-3 md:dui-aspect-video' : ''} dui-transition hover:dui-opacity-75 dui-cursor-pointer`} target="_blank" rel="noreferrer">
                  <div className={`dui-relative dui-h-full dui-w-full dui-flex dui-flex-col dui-justify-center dui-items-center`}>
                    <img src={tywLink} alt={post.title?.rendered} className={`dui-brightness-50 dui-absolute dui-w-full dui-h-full dui-left-0 dui-top-0 dui-object-cover`} />
                    <div className="dui-relative dui-text-center dui-text-white dui-p-8">
                      <h2 className="dui-font-heading !dui-font-normal dui-text-xl dui-text-white dui-text-center dui-uppercase">{post.title.rendered}</h2>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}
        {cabinPosts.length > 0 && (
          <React.Fragment>
            <Spacer />
            <h2 className="dui-text-2xl">{company} Cabin Information</h2>
            <div className='dui-mt-4 dui-grid dui-grid-cols-1 md:dui-grid-cols-3 dui-gap-8'>
              {cabinPosts.map((post, i) => {
                const tywLink = post.featured_image_src;
                return (
                  <a key={post.id} href={post.link} className={`${i === 0 ? 'md:dui-col-span-3 md:dui-aspect-video' : ''} dui-transition hover:dui-opacity-75 dui-cursor-pointer`} target="_blank" rel="noreferrer">
                    <div className={`dui-relative dui-h-full dui-w-full dui-flex dui-flex-col dui-justify-center dui-items-center`}>
                      <img src={tywLink} alt={post.title?.rendered} className={`dui-brightness-50 dui-absolute dui-w-full dui-h-full dui-left-0 dui-top-0 dui-object-cover`} />
                      <div className="dui-relative dui-text-center dui-text-white dui-p-8">
                        <h2 className="dui-font-heading !dui-font-normal dui-text-xl dui-text-white dui-text-center dui-uppercase">{post.title.rendered}</h2>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </React.Fragment>
        )}
        <Spacer />
        <h2 className="dui-text-2xl">Itinerary</h2>
        <div className="dui-mt-4 dui-min-h-[500px]">
          {booking?.id && <AppItinerary bookingId={booking.id} />}
        </div>
      </div>
    );
  }
}

ArrivalInfo.propTypes = {
  booking: PropTypes.object,
  week: PropTypes.object
};

export default withToolipModal(ArrivalInfo);
